/**
 * Dashboard Settings - User preferences for dashboard customization
 */

export interface WidgetConfig {
  id: string;
  name: string;
  description: string;
  visible: boolean;
  order: number;
}

export interface DashboardSettings {
  widgets: WidgetConfig[];
  compactMode: boolean;
  refreshInterval: number; // in seconds, 0 = manual only
}

export const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'stats', name: 'Statistics', description: 'Entity and assessment counts', visible: true, order: 0 },
  { id: 'compliance-health', name: 'Compliance Health', description: 'Compliance scores and status', visible: true, order: 1 },
  { id: 'entity-coverage', name: 'Entity Coverage', description: 'Entity types and categories', visible: true, order: 2 },
  { id: 'recent-activity', name: 'Recent Activity', description: 'Latest changes and updates', visible: true, order: 3 },
  { id: 'cost-summary', name: 'Cost Summary', description: 'Monthly costs and breakdown', visible: true, order: 4 },
  { id: 'doc-health', name: 'Documentation Health', description: 'Documentation coverage and freshness', visible: true, order: 5 },
  { id: 'reliability', name: 'Reliability Metrics', description: 'MTBF, MTTR, and availability', visible: true, order: 6 },
  { id: 'pending-matches', name: 'Pending Matches', description: 'Entities awaiting review', visible: true, order: 7 },
  { id: 'quick-actions', name: 'Quick Actions', description: 'Common tasks and shortcuts', visible: true, order: 8 },
];

export const DEFAULT_SETTINGS: DashboardSettings = {
  widgets: DEFAULT_WIDGETS,
  compactMode: false,
  refreshInterval: 0,
};

const STORAGE_KEY = 'cc-dashboard-settings';

export function loadDashboardSettings(): DashboardSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<DashboardSettings>;
      // Merge with defaults to ensure new widgets are included
      const widgets = DEFAULT_WIDGETS.map(defaultWidget => {
        const savedWidget = parsed.widgets?.find(w => w.id === defaultWidget.id);
        return savedWidget ? { ...defaultWidget, ...savedWidget } : defaultWidget;
      });
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
        widgets,
      };
    }
  } catch (err) {
    console.error('Failed to load dashboard settings:', err);
  }

  return DEFAULT_SETTINGS;
}

export function saveDashboardSettings(settings: DashboardSettings): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (err) {
    console.error('Failed to save dashboard settings:', err);
  }
}

export function resetDashboardSettings(): DashboardSettings {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
  return DEFAULT_SETTINGS;
}
